import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import { Code2, Table2, Type } from "lucide-react";
import { toast } from "sonner";

import { InviteEmailSlashExtension } from "@/lib/inviteEmailSlashExtension";
import { INVITE_EMAIL_PLACEHOLDERS } from "@/lib/inviteEmailSnippets";
import {
  clipboardHtmlFromEvent,
  normalizeInviteEmailTableHtml,
  readHtmlFromSystemClipboard,
} from "@/lib/inviteEmailPasteTable";

const SlashList = forwardRef(function SlashList({ items, command }, ref) {
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    setSelected(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelected((i) => (i + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelected((i) => (i + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        const item = items[selected];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  if (!items?.length) {
    return (
      <div className="invite-slash-menu text-sm text-slate-400 px-3 py-2">
        Nincs találat — próbáld: partner, pilot, ár
      </div>
    );
  }

  return (
    <div className="invite-slash-menu max-h-[280px] overflow-y-auto py-1">
      {items.map((item, index) => (
        <button
          key={item.id}
          type="button"
          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
            index === selected ? "bg-electric/20 text-white" : "text-slate-200 hover:bg-white/5"
          }`}
          onMouseEnter={() => setSelected(index)}
          onMouseDown={(e) => {
            e.preventDefault();
            command(item);
          }}
        >
          <div className="font-medium">{item.title}</div>
          {item.description ? (
            <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{item.description}</div>
          ) : null}
        </button>
      ))}
    </div>
  );
});

function createSlashSuggestionRenderer() {
  return {
    render: () => {
      let component;
      let popup;

      return {
        onStart: (props) => {
          component = new ReactRenderer(SlashList, {
            props,
            editor: props.editor,
          });

          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
            theme: "invite-slash",
            maxWidth: 360,
          });
        },

        onUpdate(props) {
          component.updateProps(props);
          if (popup?.[0]) {
            popup[0].setProps({ getReferenceClientRect: props.clientRect });
          }
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup?.[0]?.hide();
            return true;
          }
          return component.ref?.onKeyDown(props);
        },

        onExit() {
          popup?.[0]?.destroy();
          component?.destroy();
        },
      };
    },
  };
}

const TABLE_CELL =
  "border:1px solid #475569;padding:8px 10px;vertical-align:top;color:#e2e8f0;";
const TABLE_HEAD =
  "border:1px solid #475569;padding:8px 10px;background:#1e293b;font-weight:bold;color:#f8fafc;";

export function InviteEmailRichEditor({ value, onChange, "data-testid": testId }) {
  const [showSource, setShowSource] = useState(false);
  const editorRef = useRef(null);

  const slashExtension = useMemo(
    () => InviteEmailSlashExtension(createSlashSuggestionRenderer()),
    [],
  );

  const insertTableHtml = useCallback((rawHtml, { atEnd = false } = {}) => {
    const ed = editorRef.current;
    const normalized = normalizeInviteEmailTableHtml(rawHtml);
    if (!normalized) {
      toast.error("Nem találtunk táblázatot a vágólapon.");
      return false;
    }
    if (showSource || !ed) {
      onChange?.(`${value || ""}${value ? "\n" : ""}${normalized}`);
      toast.success("Táblázat beillesztve (HTML).");
      return true;
    }
    const chain = ed.chain().focus();
    if (atEnd) {
      chain.setTextSelection(ed.state.doc.content.size);
    }
    chain.insertContent(normalized).run();
    toast.success("Táblázat beillesztve.");
    return true;
  }, [onChange, showSource, value]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-electric underline" },
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          style: "border-collapse:collapse;width:100%;margin:14px 0;font-size:14px;",
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: { style: TABLE_HEAD },
      }),
      TableCell.configure({
        HTMLAttributes: { style: TABLE_CELL },
      }),
      Placeholder.configure({
        placeholder: "Írd a levelet… / sablonok · Ctrl+V táblázat (Excel, Sheets)",
      }),
      slashExtension,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "invite-email-editor prose prose-invert prose-sm max-w-none min-h-[240px] px-3 py-3 focus:outline-none " +
          "prose-p:my-2 prose-ul:my-2 prose-li:my-0.5 prose-a:text-electric prose-table:my-3",
        "data-testid": testId || "invite-body",
      },
      handlePaste: (view, event) => {
        const raw = clipboardHtmlFromEvent(event);
        if (!raw) return false;
        const normalized = normalizeInviteEmailTableHtml(raw);
        if (!normalized) return false;
        event.preventDefault();
        const ed = editorRef.current;
        if (ed) {
          ed.chain().focus().insertContent(normalized).run();
        } else {
          view.dispatch(view.state.tr.insertText(""));
        }
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!editor || value == null) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  const insertPlaceholder = (token) => {
    editor?.chain().focus().insertContent(token).run();
  };

  const pasteTableFromClipboard = async () => {
    const html = await readHtmlFromSystemClipboard();
    if (html) {
      insertTableHtml(html);
      return;
    }
    toast.message("Ha a böngésző nem engedi a vágólap olvasást: másold ki a táblázatot, kattints a szerkesztőbe, Ctrl+V.");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1">
          <Type className="w-3.5 h-3.5" />
          <kbd className="px-1.5 py-0.5 rounded bg-white/10 font-mono">/</kbd> sablonok
        </span>
        <span className="text-white/20">|</span>
        {INVITE_EMAIL_PLACEHOLDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="px-2 py-0.5 rounded-full bg-white/5 hover:bg-electric/20 hover:text-electric font-mono"
            onClick={() => insertPlaceholder(p.token)}
          >
            {p.token}
          </button>
        ))}
        <button
          type="button"
          data-testid="invite-paste-table"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 hover:bg-electric/20 hover:text-electric"
          onClick={pasteTableFromClipboard}
        >
          <Table2 className="w-3.5 h-3.5" />
          Táblázat beillesztése
        </button>
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 hover:bg-white/10"
          onClick={() => setShowSource((s) => !s)}
        >
          <Code2 className="w-3.5 h-3.5" />
          {showSource ? "Vizuális" : "HTML"}
        </button>
      </div>

      {!showSource ? (
        <div className="rounded-lg border border-white/10 bg-anthracite-900 overflow-hidden">
          <EditorContent editor={editor} />
        </div>
      ) : (
        <textarea
          data-testid={testId}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          rows={12}
          className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-3 text-sm font-mono text-xs"
        />
      )}

      <style>{`
        .tippy-box[data-theme~='invite-slash'] {
          background: rgb(30 32 38);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.75rem;
          box-shadow: 0 12px 40px rgba(0,0,0,0.45);
        }
        .tippy-box[data-theme~='invite-slash'] .tippy-content {
          padding: 0;
        }
        .invite-email-editor p.is-editor-empty:first-child::before {
          color: rgb(100 116 139);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .invite-email-editor table {
          border-collapse: collapse;
          width: 100%;
        }
        .invite-email-editor td,
        .invite-email-editor th {
          border: 1px solid #475569;
          padding: 8px 10px;
          vertical-align: top;
        }
        .invite-email-editor th {
          background: #1e293b;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
